/* src/ui/hud/viewToggles.ts — WORLD / FOOT TRAFFIC / LOGISTICS / RESOURCES (TASKS.md 1.6).
 *
 * This component's entire job is writing `ui.viewMode` and `ui.overlays` to the store. What
 * changes on the canvas is render/'s concern, reached through core/events.ts — never by
 * importing render/ from here, which would break the module boundary in PLAN.md Part 2 §1.
 *
 * FOOT TRAFFIC, LOGISTICS and RESOURCES have no data behind them until Phases 3, 6 and 5
 * respectively. They are rendered disabled rather than hidden, because the mode row is a map
 * of the game and hiding three-quarters of it teaches the player the wrong shape.
 */

import { uiStore, type ViewMode } from '../../core/store.ts';
import { Component } from '../component.ts';
import { registerAction } from '../dispatch.ts';
import { el } from '../dom.ts';

const MODES: ReadonlyArray<{ readonly id: ViewMode; readonly enabled: boolean; readonly hint: string }> = [
  { id: 'WORLD', enabled: true, hint: 'The city as it is' },
  { id: 'FOOT TRAFFIC', enabled: false, hint: 'Unlocks in Phase 3' },
  { id: 'LOGISTICS', enabled: false, hint: 'Unlocks in Phase 6' },
  { id: 'RESOURCES', enabled: false, hint: 'Unlocks in Phase 5' },
];

const OVERLAYS: ReadonlyArray<{ readonly id: string; readonly label: string }> = [
  { id: 'labels', label: 'Labels' },
  { id: 'grid', label: 'Chunk grid' },
];

export class ViewToggles extends Component<void> {
  readonly #buttons = new Map<ViewMode, HTMLElement>();
  readonly #overlayInputs = new Map<string, HTMLInputElement>();

  constructor() {
    super(undefined);
  }

  protected render(): HTMLElement {
    const modeRow = el('div', {
      class: 'gf-viewtoggles',
      attrs: { role: 'radiogroup', 'aria-label': 'View mode' },
    });

    for (const mode of MODES) {
      const button = el(
        'button',
        {
          class: `gf-vt${mode.enabled ? '' : ' is-disabled'}`,
          attrs: {
            type: 'button',
            role: 'radio',
            'aria-checked': mode.id === 'WORLD',
            title: mode.hint,
            ...(mode.enabled ? {} : { disabled: true }),
          },
          data: mode.enabled ? { action: 'view:mode', mode: mode.id } : {},
        },
        mode.id,
      );
      this.#buttons.set(mode.id, button);
      modeRow.appendChild(button);
    }

    const overlayRow = el('div', {
      class: 'gf-overlaytoggles',
      attrs: { 'aria-label': 'Overlays' },
    });

    for (const overlay of OVERLAYS) {
      const input = el('input', {
        attrs: { type: 'checkbox', id: `gf-ov-${overlay.id}` },
        // Checkboxes need their own listener: `change` does not bubble usefully through a
        // click delegator, and this is exactly the escape hatch dom.ts documents.
        on: {
          change: () => {
            this.#setOverlay(overlay.id, input.checked);
          },
        },
      });
      input.checked = uiStore.get().overlays[overlay.id] ?? false;
      this.#overlayInputs.set(overlay.id, input);

      overlayRow.appendChild(
        el(
          'label',
          { class: 'gf-ov' },
          input,
          el('span', null, overlay.label),
        ),
      );
    }

    return el('div', { class: 'gf-viewstack' }, modeRow, overlayRow);
  }

  protected override onMount(): void {
    this.onCleanup(
      registerAction('view:mode', (_e, target) => {
        const mode = target.dataset['mode'] as ViewMode | undefined;
        if (mode) this.#setMode(mode);
      }),
    );

    this.onCleanup(
      uiStore.select(
        (s) => s.viewMode,
        (mode) => {
          for (const [id, button] of this.#buttons) {
            const active = id === mode;
            button.classList.toggle('is-active', active);
            button.setAttribute('aria-checked', String(active));
          }
        },
      ),
    );

    this.#buttons.get(uiStore.get().viewMode)?.classList.add('is-active');
  }

  #setMode(mode: ViewMode): void {
    const current = uiStore.get();
    if (current.viewMode === mode) return;
    uiStore.set({ ...current, viewMode: mode });
  }

  #setOverlay(id: string, value: boolean): void {
    const current = uiStore.get();
    uiStore.set({ ...current, overlays: { ...current.overlays, [id]: value } });
  }
}
